import { CheckIcon, XIcon } from '@primer/octicons-react';
import React from 'react';
import { graphql, useFragment } from 'react-relay';

import {
  BrandMessageTemplateDescriptionList_brandMessageTemplate$data,
  BrandMessageTemplateDescriptionList_brandMessageTemplate$key,
} from '../../../relay/__generated__/BrandMessageTemplateDescriptionList_brandMessageTemplate.graphql';
import DateText from '../../core/DateText';
import DescriptionList, { DescriptionListProps } from '../../core/DescriptionList';
import Grid from '../../core/Grid';
import Image from '../../core/Image';
import Label from '../../core/Label';
import Link from '../../core/Link';
import Stack from '../../core/Stack';
import StyledOcticon from '../../core/StyledOcticon';
import Text from '../../core/Text';
import View from '../../core/View';
import BrandMessageTemplatePreview from '../BrandMessageTemplatePreview';
import { getBrandMessageTypeLabel } from '../constants';

const BrandMessageTemplateDescriptionList_brandMessageTemplate = graphql`
  fragment BrandMessageTemplateDescriptionList_brandMessageTemplate on BrandMessageTemplate {
    id
    createTime
    serviceStatus
    updateTime

    templateCode
    templateName
    chatBubbleType
    parameterNames

    adult
    imageLink
    imageUrl
    content
    header
    additionalContent
    carousel
    wideItemList
    video
    commerce

    buttons {
      name
      linkMobile
      linkType
      linkIos
    }
    coupon {
      title
      description
      linkMobile
      linkPc
      linkAndroid
      linkIos
    }
  }
`;

type Props = {
  brandMessageTemplate: BrandMessageTemplateDescriptionList_brandMessageTemplate$key;
  type?: 'default' | 'activity' | 'preview';
} & Omit<
  DescriptionListProps<BrandMessageTemplateDescriptionList_brandMessageTemplate$data>,
  'item' | 'itemDescriptions' | 'picks'
>;
const BrandMessageTemplateDescriptionList = ({
  brandMessageTemplate: brandMessageTemplateReference,
  type = 'default',
  ...props
}: Props) => {
  const brandMessageTemplate = useFragment(
    BrandMessageTemplateDescriptionList_brandMessageTemplate,
    brandMessageTemplateReference,
  );

  return (
    <DescriptionList
      item={brandMessageTemplate}
      itemDescriptions={{
        status: {
          title: '상태',
          renderValue: ({ serviceStatus }) =>
            serviceStatus === 'A' ? (
              <StyledOcticon icon={CheckIcon} sx={{ color: 'success.fg' }} />
            ) : (
              <StyledOcticon icon={XIcon} sx={{ color: 'danger.fg' }} />
            ),
        },
        createTime: {
          title: '제작일',
          renderValue: ({ createTime }) => <DateText fontSize={1}>{createTime}</DateText>,
        },
        modified: {
          title: '수정일',
          renderValue: ({ updateTime }) => <DateText fontSize={1}>{updateTime}</DateText>,
        },
        templateCode: {
          title: '템플릿 코드',
          renderValue: ({ templateCode }) => (
            <Text fontSize={1} sx={{ wordBreak: 'break-all' }}>
              {templateCode}
            </Text>
          ),
        },
        type: {
          title: '메시지 유형',
          renderValue: ({ chatBubbleType }) => <Text fontSize={1}>{getBrandMessageTypeLabel(chatBubbleType)}</Text>,
        },
        parameterNames: {
          title: '변수명',
          renderValue: ({ parameterNames }) => (
            <Stack gapX={1} gapY={1} wrap={true}>
              {parameterNames.map((name) => (
                <Stack.Item key={name}>
                  <Label variant={'default'}>{name}</Label>
                </Stack.Item>
              ))}
            </Stack>
          ),
        },
        image: {
          title: '이미지',
          renderValue: ({ imageLink, imageUrl }) => {
            const imageComponent = <Image html src={imageUrl || ''} alt={'브랜드 메세지 이미지'} />;
            return (
              <View>
                <View maxWidth={300} lineHeight={0}>
                  {imageLink ? (
                    <Link target={'_blank'} href={imageLink}>
                      {imageComponent}
                    </Link>
                  ) : (
                    imageComponent
                  )}
                </View>
                <Text fontSize={0} color={'fg.muted'} sx={{ wordBreak: 'break-all' }}>
                  {imageLink ? `(${imageLink})` : '-'}
                </Text>
              </View>
            );
          },
        },
        content: {
          title: '본문',
          renderValue: ({ content }) => (
            <Text fontSize={1} whiteSpace={'pre-wrap'}>
              {content}
            </Text>
          ),
        },
        buttons: {
          title: '버튼',
          renderValue: ({ buttons: _buttons }) => {
            const buttons = _buttons ?? [];

            if (buttons.length === 0) return undefined;
            else
              return buttons.map(({ name, linkMobile, linkType, linkIos }, i) => (
                <View key={name + linkMobile} mt={i === 0 ? 0 : 2}>
                  <Grid gapX={1} wrap={[true, false, false]} sx={{ alignItems: 'baseline' }}>
                    <Grid.Unit size={'min'}>
                      <View width={185} p={1}>
                        <Text fontSize={1}>{name}</Text>
                      </View>
                    </Grid.Unit>
                    <Grid.Unit size={'max'}>
                      <Text fontSize={0} color={'fg.muted'} sx={{ wordBreak: 'break-all' }}>
                        ({linkType === 'AL' ? linkIos : linkMobile})
                      </Text>
                    </Grid.Unit>
                  </Grid>
                </View>
              ));
          },
        },
        preview: {
          title: '미리보기',
          renderValue: (template) => <BrandMessageTemplatePreview value={template} />,
        },
      }}
      picks={
        type === 'default'
          ? ['templateCode', 'type']
          : type === 'activity'
            ? ['status', 'createTime', 'modified']
            : ['preview', 'parameterNames']
      }
      {...props}
    />
  );
};

export default BrandMessageTemplateDescriptionList;
